import { useEffect, useState } from "react"
import FieldInfoIcon from "../../components/field-info"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../components/ui/form"
import { Input } from "../../components/ui/input"
import { cn, parseAttributes } from "../../lib/utils"
import { useFormStore } from "../../stores/use-form-store"
import {
	EmailInputConfig,
	type Attribute,
	type FieldProps,
	type LEAD_DETAILS,
} from "../../types/form-types"
import { Description } from "./components/description"
import OTPInputField from "./components/otp-field"
import { USER_STORAGE_NAME } from "@/lib/constants"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

export const Email = ({ form, field, commonAttributes }: FieldProps) => {
	const { label, infoIconText, infoIconType, description, infoIconRequired } =
		commonAttributes

	const { validateWithOTP, resendOTPTimer } = getEmailInputAttributes(
		field.attributes,
	)
	const { verifiedFields, shouldDisable, setLeadDetails } = useFormStore()
	const isVerified = verifiedFields.get(field.field_id) || false
	const { config, isPreview } = useFormStore()
	const [isTimerExpired, setIsTimerExpired] = useState(true)

	useEffect(() => {
		if (
			field.field_type === "EMAIL" &&
			field.attributes.find(attr => attr.name === "alias")?.value ===
				"email"
		) {
			const fieldValue = form.getValues(field.field_id)
			if (!fieldValue && config?.type === "FORM" && !isPreview) {
				const leadDetails = JSON.parse(
					localStorage.getItem(USER_STORAGE_NAME) || "{}",
				) as { state: { user: LEAD_DETAILS } } // please sync the type with student portal auth store
				if (!leadDetails) return
				const email = leadDetails?.state?.user?.email || ""
				if (email) {
					form.setValue(field.field_id, email)
				}
			}
			setLeadDetails({
				email_field_id: field.field_id,
			})
		}
	}, [])

	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			name={field.field_id}
			control={form.control}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<FormControl>
						<div>
							<Input
								disabled={
									isVerified ||
									shouldDisable ||
									!isTimerExpired
								}
								{...formField}
								onChange={e =>
									formField.onChange(
										e.target.value.toLowerCase(),
									)
								}
								type="email"
							/>
							{config?.type === "FORM" &&
								validateWithOTP &&
								!isVerified && (
									<OTPInputField
										resendOTPTimer={resendOTPTimer || 120}
										type="EMAIL"
										email={formField.value}
										form={form}
										field={field}
										setIsTimerExpired={setIsTimerExpired}
									/>
								)}
							{config?.type === "FORM" &&
								validateWithOTP &&
								isVerified && (
									<p className="text-green-500">verified</p>
								)}
						</div>
					</FormControl>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getEmailInputAttributes(attributes: Attribute[]) {
	const props: {
		domainRestriction?: string[]
		validateWithOTP?: boolean
		resendOTPTimer?: number
	} = {}

	const parsedAttributes = parseAttributes(attributes)
	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case EmailInputConfig.ValidateWithOTP:
				props.validateWithOTP = value as boolean
				break
			case EmailInputConfig.ResendOTPTimer:
				props.resendOTPTimer = value as number
				break
		}
	})

	return props
}
